<template>
    <QuillEditor v-if="quillReady" ref="editorRef" :content="localContent" :contentType="contentType" :theme="theme"
        :readOnly="readOnly" :placeholder="placeholder" :options="editorOptions" v-bind="forwardedAttrs"
        class="bg-white" @ready="handleReady" @update:content="handleEditorUpdate" />
</template>

<script setup>
import { ref, watch, nextTick, computed, useAttrs, onMounted } from 'vue'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { createCustomQuillOptions, convertLegacyQuillContent, registerQuillFormats } from '../../utils/quillConfig'

const props = defineProps({
    content: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: 'Write your content here...'
    },
    allowImages: {
        type: Boolean,
        default: true
    },
    allowVideos: {
        type: Boolean,
        default: true
    },
    allowDocuments: {
        type: Boolean,
        default: true
    },
    toolbar: {
        type: Array,
        default: null
    },
    formats: {
        type: Array,
        default: null
    },
    defaultFont: {
        type: String,
        default: ''
    },
    defaultSize: {
        type: String,
        default: ''
    },
    theme: {
        type: String,
        default: 'snow'
    },
    contentType: {
        type: String,
        default: 'html'
    },
    readOnly: {
        type: Boolean,
        default: false
    },
    handlers: {
        type: Object,
        default: null
    }
})

const emit = defineEmits(['update:content', 'ready'])

const forwardedAttrs = useAttrs()
const editorRef = ref(null)

// vue-quill 1.5.x lazy-loads Quill. Register our custom formats/modules (which
// need the Quill class) before the QuillEditor child mounts and instantiates
// Quill — gate rendering on quillReady so registration always happens first.
const quillReady = ref(false)
onMounted(async () => {
    try {
        await registerQuillFormats()
    } finally {
        quillReady.value = true
    }
})

// Raw Quill instance, captured on the editor's 'ready' event.
const quillRef = ref(null)

// localContent drives the :content binding on QuillEditor. It is intentionally
// NOT updated during user typing (handleEditorUpdate only emits outward) so the
// editor is never re-set under the cursor mid-keystroke. It is updated only by
// external props.content changes; the isFromEditor flag stops that watcher from
// reacting to our own outbound emit. @vueup/vue-quill (1.5.x / Quill 2) owns
// applying content to the editor — we no longer call setContents ourselves
// (doing so double-applied content and desynced Quill 2's selection model).
let isFromEditor = false
const localContent = ref(convertLegacyQuillContent(props.content ?? ''))

watch(() => props.content, newVal => {
    if (isFromEditor) return
    const converted = convertLegacyQuillContent(newVal ?? '')
    if (converted !== localContent.value) {
        localContent.value = converted
    }
}, { immediate: false })

const handleEditorUpdate = value => {
    isFromEditor = true
    emit('update:content', value ?? '')
    nextTick(() => { isFromEditor = false })
}

const editorOptions = computed(() => {
    const options = createCustomQuillOptions({
        getEditor: () => editorRef.value,
        placeholder: props.placeholder,
        allowImages: props.allowImages,
        allowVideos: props.allowVideos,
        allowDocuments: props.allowDocuments,
        toolbar: props.toolbar ?? undefined,
        formats: props.formats ?? undefined,
        defaultFont: props.defaultFont || undefined,
        defaultSize: props.defaultSize || undefined
    })

    if (props.handlers && options?.modules?.toolbar) {
        options.modules.toolbar.handlers = {
            ...(options.modules.toolbar.handlers || {}),
            ...props.handlers
        }
    }

    return options
})

const handleReady = quillInstance => {
    quillRef.value = quillInstance
    emit('ready', quillInstance)
}

const setHTML = html => {
    // Let @vueup/vue-quill apply it via the :content binding.
    localContent.value = convertLegacyQuillContent(html ?? '')
}

const getHTML = () => {
    return editorRef.value?.getHTML?.() ?? ''
}

const getText = () => {
    return editorRef.value?.getText?.() ?? ''
}

const getQuill = () => {
    return quillRef.value ?? editorRef.value?.getQuill?.() ?? null
}

const focus = () => {
    editorRef.value?.focus?.()
}

const blur = () => {
    editorRef.value?.blur?.()
}

defineExpose({ setHTML, getHTML, getText, getQuill, focus, blur })
</script>
